// File: frontend/src/services/profileService.ts
// src/services/profileService.ts

import { ProfileModel, EntryModel } from "../models";
import { API_BASE_URL } from "../constants";
import { ProfileDetailsModel, UpdateProfileDetailsRequest } from "../models/ProfileModel";
import { downscaleImage } from "../utils/imageDownscale";
import { mapRawEntry as parseEntryJson } from "./entryMapper";


// Profile entries response type
export interface ProfileEntriesResponse {
  entries: EntryModel[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

// Public tab types (Genel Bilgiler)
export type PublicProfileTab = "recent" | "mostLiked" | "mostDisliked" | "mostFavorited" | "approvedTopics";

// Private tab types
export type PrivateProfileTab = "favorites" | "liked" | "disliked" | "following" | "blocked" | "rejectedTopics";

// ── Custom topic suggestions (profile lists) ──

// A single item of the user's approved/rejected topic suggestion lists
export interface UserTopicSuggestionItem {
  id: number;
  // Original title proposed by the user
  title: string;
  // Title actually published on approval (differs when admin edited it); null for rejected
  publishedTitle: string | null;
  status: "APPROVED" | "REJECTED";
  // Rejection note from the reviewing admin/mod — only present on the rejected tab
  adminNote: string | null;
  // Published topic link data — null for rejected items or unlinked legacy rows
  topicId: number | null;
  topicSlug: string | null;
  // Current title of the published topic (preferred for display — admins may rename)
  topicTitle: string | null;
  categoryName: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export interface UserTopicSuggestionsResponse {
  items: UserTopicSuggestionItem[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

/**
 * Fetch a user's custom topic suggestions for the profile page.
 *
 * @param userId User ID whose suggestions to fetch
 * @param tab "approved" (public) or "rejected" (owner-only, includes adminNote)
 * @param page Page number (1-indexed, converted to 0-indexed for the API)
 * @param pageSize Number of items per page
 */
export const fetchUserTopicSuggestions = async (
  userId: number,
  tab: "approved" | "rejected",
  page: number = 1,
  pageSize: number = 10
): Promise<UserTopicSuggestionsResponse> => {
  const url = `${API_BASE_URL}/api/profile/${userId}/topic-suggestions?tab=${tab}&page=${page - 1}&size=${pageSize}`;

  // Token is required for the rejected tab; harmless for approved
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });

  if (response.status === 403) {
    throw new Error("Access denied - you can only view your own rejected suggestions");
  }
  if (!response.ok) {
    // Include status and body: a 4xx/5xx here used to be swallowed by the caller
    // and rendered as "no approved topics", hiding real backend failures
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Failed to fetch topic suggestions (HTTP ${response.status}) ${detail}`.trim()
    );
  }

  const json = await response.json();
  return {
    items: (json.content ?? []).map((s: any): UserTopicSuggestionItem => ({
      id: s.id,
      title: s.title,
      publishedTitle: s.publishedTitle ?? null,
      status: s.status,
      adminNote: s.adminNote ?? null,
      topicId: s.topicId ?? null,
      topicSlug: s.topicSlug ?? null,
      topicTitle: s.topicTitle ?? null,
      categoryName: s.categoryName ?? null,
      createdAt: s.createdAt,
      reviewedAt: s.reviewedAt ?? null,
    })),
    currentPage: (json.currentPage ?? 0) + 1,
    totalPages: json.totalPages ?? 0,
    totalItems: json.totalItems ?? 0,
  };
};

/**
 * Build auth headers from localStorage token.
 * Returns an empty object when no token is present.
 */
const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Resolve a media URL returned by the backend.
 * When the backend is configured with MEDIA_PUBLIC_BASE_URL it returns a fully
 * qualified URL (starts with "http") which is used as-is.
 * For local dev the backend returns a root-relative path like "/media/users/..."
 * which is prepended with the API base URL so the browser can reach the file.
 */
export const resolveMediaUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${API_BASE_URL}${url}`;
};

/**
 * Fetch user profile by user ID.
 * Sends auth token so the backend can populate vote data on the pinned entry.
 */
export const fetchProfileById = async (userId: number): Promise<ProfileModel> => {
  const response = await fetch(`${API_BASE_URL}/api/profile/${userId}`, {
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch profile");
  }

  return parseProfileResponse(await response.json());
};

/**
 * Fetch user profile by username.
 */
export const fetchProfileByUsername = async (username: string): Promise<ProfileModel> => {
  const response = await fetch(`${API_BASE_URL}/api/profile/username/${username}`, {
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch profile");
  }

  return parseProfileResponse(await response.json());
};

/**
 * Fetch user's public entries with tab filter (Genel Bilgiler).
 * 
 * @param userId User ID whose entries to fetch
 * @param tab Tab filter: "recent", "mostLiked", "mostDisliked", or "mostFavorited"
 * @param page Page number (1-indexed)
 * @param pageSize Number of entries per page
 */
export const fetchPublicProfileEntries = async (
  userId: number,
  tab: PublicProfileTab = "recent",
  page: number = 1,
  pageSize: number = 10
): Promise<ProfileEntriesResponse> => {
  const url = `${API_BASE_URL}/api/profile/${userId}/entries?tab=${tab}&page=${page - 1}&size=${pageSize}`;
  
  // Send token so backend can determine currentUserRating and currentUserHasFav
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch profile entries");
  }

  return parseEntriesResponse(await response.json(), page, pageSize);
};

/**
 * Fetch user's private entries with tab filter (Bana Özel).
 * Only accessible by the profile owner.
 * 
 * @param userId User ID whose entries to fetch
 * @param tab Tab filter: "favorites", "liked", or "disliked"
 * @param page Page number (1-indexed)
 * @param pageSize Number of entries per page
 */
export const fetchPrivateProfileEntries = async (
  userId: number,
  tab: PrivateProfileTab = "favorites",
  page: number = 1,
  pageSize: number = 10
): Promise<ProfileEntriesResponse> => {
  const url = `${API_BASE_URL}/api/profile/${userId}/private?tab=${tab}&page=${page - 1}&size=${pageSize}`;
  
  // Token is required for private data
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Authentication required for private profile data");
  }
  
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });

  if (response.status === 403) {
    throw new Error("Access denied - you can only view your own private data");
  }
  
  if (!response.ok) {
    throw new Error("Failed to fetch private profile entries");
  }

  return parseEntriesResponse(await response.json(), page, pageSize);
};

/**
 * Fetch the entries of a user that contain inline images — source of the
 * "Albüm" tab on the profile page.
 *
 * The page unit is the ENTRY, not the image: images live inside the entry
 * content as (gorsel: ...) tokens, so one returned entry can expand into
 * several album tiles. Returning whole entries also gives the album the topic
 * title and the entry link for every tile without a separate DTO.
 *
 * @param userId User ID whose album to fetch
 * @param page Page number (1-indexed, converted to 0-indexed for the API)
 * @param pageSize Number of entries per page
 */
export const fetchProfileAlbum = async (
  userId: number,
  page: number = 1,
  pageSize: number = 12
): Promise<ProfileEntriesResponse> => {
  const url = `${API_BASE_URL}/api/profile/${userId}/album?page=${page - 1}&size=${pageSize}`;

  // Token is optional here — it only enriches the vote fields of the entries
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });

  if (!response.ok) {
    // Surface the status: a failed request and an empty album otherwise look
    // identical in the UI ("henüz görsel yüklenmemiş")
    const detail = await response.text().catch(() => "");
    throw new Error(`Failed to fetch profile album (HTTP ${response.status}) ${detail}`.trim());
  }

  return parseEntriesResponse(await response.json(), page, pageSize);
};

/**
 * Update the current user's bio (personal message).
 * Pass null or empty string to clear the bio.
 */
export const updateBio = async (bio: string | null): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/profile/me/bio`, {
    method: "PUT",
    headers: {
      "Content-Type": "text/plain",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: bio ?? "",
  });

  if (!response.ok && response.status !== 204) {
    // Read the backend error body to get the actual error code (e.g. "BIO_TOO_LONG:1")
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to update bio");
  }
};

// ===== Avatar / Banner Upload =====

/**
 * Upload or replace the current user's avatar.
 * Returns the public URL of the newly saved image.
 *
 * @param file JPEG / PNG / WebP file, max 2 MB
 */
export const uploadAvatar = async (file: File): Promise<string> => {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}/api/profile/me/avatar`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "UPLOAD_FAILED");
  }

  const data = await response.json();
  // Resolve to absolute URL in case backend returned a root-relative path
  return resolveMediaUrl(data.url) ?? data.url;
};

/**
 * Delete the current user's avatar and remove the file from the server.
 */
export const deleteAvatar = async (): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/profile/me/avatar`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok && response.status !== 204) {
    throw new Error("Failed to delete avatar");
  }
};

/**
 * Upload or replace the current user's banner (cover photo).
 * Returns the public URL of the newly saved image.
 *
 * @param file JPEG / PNG / WebP file, max 5 MB
 */
export const uploadBanner = async (file: File): Promise<string> => {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE_URL}/api/profile/me/banner`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "UPLOAD_FAILED");
  }

  const data = await response.json();
  return resolveMediaUrl(data.url) ?? data.url;
};

/**
 * Delete the current user's banner and remove the file from the server.
 */
export const deleteBanner = async (): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/profile/me/banner`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok && response.status !== 204) {
    throw new Error("Failed to delete banner");
  }
};

// ===== Issue C: Pin / Unpin =====

/**
 * Pin one of the user's own entries to their profile.
 * Returns the updated ProfileDto so the UI can update immediately.
 *
 * @param entryId The entry to pin
 */
export const pinEntry = async (entryId: number): Promise<ProfileModel> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/profile/pin/${entryId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || "Failed to pin entry");
  }

  return parseProfileResponse(await response.json());
};

/**
 * Unpin the current user's own pinned entry.
 */
export const unpinEntry = async (): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/profile/pin`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });

  if (!response.ok && response.status !== 204) {
    throw new Error("Failed to unpin entry");
  }
};

/**
 * Admin or moderator removes a pinned entry from someone else's profile.
 *
 * @param targetUserId The profile owner whose pin is being removed
 */
export const adminRemovePin = async (targetUserId: number): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/profile/${targetUserId}/pin`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });

  if (!response.ok && response.status !== 204) {
    throw new Error("Failed to remove pinned entry");
  }
};

// ===== Parse Helpers =====

/**
 * Parse a profile API response into a ProfileModel.
 * Maps the pinnedEntry field from backend format to EntryModel.
 */
const parseProfileResponse = (json: any): ProfileModel => ({
  id: json.id,
  username: json.username,
  status: json.status,
  role: json.role,
  registrationDate: json.registrationDate,
  subtitle: json.subtitle ?? null,
  // Nickaltı topic slug — null for legacy accounts not yet backfilled by the migration
  nickaltiSlug: json.nickaltiSlug ?? null,
  // Verified badge flag — default to false when the backend omits it
  isVerified: json.isVerified ?? false,
  bio: json.bio ?? null,
  bioBlocked: json.bioBlocked ?? false,
  bioBlockReason: json.bioBlockReason ?? null,
  // Resolve media URLs: backend may return root-relative paths in local dev
  avatarUrl: resolveMediaUrl(json.avatarUrl ?? null),
  coverImageUrl: resolveMediaUrl(json.coverImageUrl ?? null),
  entryCount: json.entryCount ?? 0,
  followerCount: json.followerCount ?? 0,
  followingCount: json.followingCount ?? 0,
  karma: json.karma ?? 0,
  daysLoggedIn: json.daysLoggedIn ?? 0,
  consecutiveDays: json.consecutiveDays ?? 0,
  // Custom topic suggestion chips — total/rejected are 0 for non-owner viewers
  topicSuggestionApprovedCount: json.topicSuggestionApprovedCount ?? 0,
  topicSuggestionTotalCount: json.topicSuggestionTotalCount ?? 0,
  topicSuggestionRejectedCount: json.topicSuggestionRejectedCount ?? 0,
  isFollowing: json.isFollowing ?? false,
  isBlocked: json.isBlocked ?? false,
  hasBlockedMe: json.hasBlockedMe ?? false,
  // Messaging eligibility for the viewer — default to false so the message button
  // stays hidden rather than appearing on a response that omits the flag
  canMessage: json.canMessage ?? false,
  pinnedEntry: json.pinnedEntry ? parseEntryJson(json.pinnedEntry) : null,
  deletionRequestedAt: json.deletionRequestedAt ?? null,
  scheduledDeletionAt: json.scheduledDeletionAt ?? null,
  // Non-null once a self-deletion request has been carried out and the account was
  // closed — drives the tombstone banner on the profile page
  closedAt: json.closedAt ?? null,
  freezeRequestedAt: json.freezeRequestedAt ?? null,
  suspensionEndDate: json.suspensionEndDate ?? null,
  // Only sent for BANNED accounts; null everywhere else, including rows banned
  // before the field existed — the banner falls back to its undated wording.
  bannedAt: json.bannedAt ?? null,
  // Only sent for DELETED_BY_ADMIN accounts; null everywhere else, including rows
  // removed before the field existed — the banner falls back to its undated wording.
  deletedByAdminAt: json.deletedByAdminAt ?? null,
  favoriteClubs: json.favoriteClubs ?? [],
});


/**
 * Parse entries response from backend.
 */
const parseEntriesResponse = (
  responseJson: any, 
  page: number, 
  pageSize: number
): ProfileEntriesResponse => {
  // Calculate total pages on client side for consistency
  const calculatedTotalPages = Math.ceil(responseJson.totalItems / pageSize);

  return {
    entries: responseJson.content.map(parseEntryJson),
    currentPage: page,
    totalPages: calculatedTotalPages,
    totalItems: responseJson.totalItems,
  };
};

// ===== Entry Inline Image Upload =====

/**
 * Progress callback for uploads. `percent` is 0–100 and only meaningful
 * while `computable` is true (a few browsers never report a total).
 */
export interface UploadProgress {
  percent: number;
  loaded: number;
  total: number;
  computable: boolean;
}

export interface UploadEntryImageOptions {
  /** Called repeatedly while the bytes are being sent. */
  onProgress?: (progress: UploadProgress) => void;
  /** Called once, right after the file has been downscaled and the request
      is about to start — carries the file actually being sent. */
  onStart?: (file: File) => void;
  /** Abort handle. Aborting rejects the promise with "UPLOAD_ABORTED". */
  signal?: AbortSignal;
}

/**
 * Upload an image for inline embedding inside an entry.
 * Returns the OPAQUE image id to embed as (gorsel: id) — e.g.
 * "8f14e45fceea167a5a36dedd4bea2543.jpg". The id carries no directory path,
 * no user id and no timestamp, so the editor text never reveals where the
 * file is stored on the server. The id is turned back into a real URL at
 * render time by utils/entryImageUrl.resolveEntryImageSrc.
 *
 * Uses XMLHttpRequest rather than fetch because fetch cannot report upload
 * progress: it emits nothing at all until the whole request has completed,
 * which left the user staring at an unchanged screen on slow connections.
 * The file is downscaled first — see utils/imageDownscale.
 *
 * @param file JPEG / PNG / WebP file, max 5 MB
 * @param options progress, start and abort hooks (all optional)
 */
export const uploadEntryImage = async (
  file: File,
  options: UploadEntryImageOptions = {}
): Promise<string> => {
  const { onProgress, onStart, signal } = options;

  if (signal?.aborted) {
    throw new Error("UPLOAD_ABORTED");
  }

  // Shrink before sending — never fails, returns the original on any problem
  const payload = await downscaleImage(file);
  onStart?.(payload);

  if (signal?.aborted) {
    throw new Error("UPLOAD_ABORTED");
  }

  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("file", payload);

  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/api/media/entry-image`);
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    // Needed so xhr.response can be read as text for the error branches
    xhr.responseType = "text";

    const onAbort = () => xhr.abort();
    signal?.addEventListener("abort", onAbort);

    const cleanup = () => signal?.removeEventListener("abort", onAbort);

    xhr.upload.onprogress = (e) => {
      if (!onProgress) return;
      const total = e.lengthComputable ? e.total : 0;
      onProgress({
        percent: e.lengthComputable ? Math.round((e.loaded / e.total) * 100) : 0,
        loaded: e.loaded,
        total,
        computable: e.lengthComputable,
      });
    };

    xhr.onload = () => {
      cleanup();
      let data: any = {};
      try {
        data = JSON.parse(xhr.responseText || "{}");
      } catch {
        data = {};
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(data.message || "UPLOAD_FAILED"));
        return;
      }

      // Current backend returns the opaque id in `id`. The `url` fallback keeps
      // an older backend build working during a rolling deploy: it is a
      // root-relative path that still has to be resolved to an absolute URL.
      if (typeof data.id === "string" && data.id.length > 0) {
        resolve(data.id);
        return;
      }
      resolve(resolveMediaUrl(data.url) ?? data.url);
    };

    // Network-level failure: no connection, DNS, CORS, server unreachable
    xhr.onerror = () => {
      cleanup();
      reject(new Error("UPLOAD_NETWORK_ERROR"));
    };

    xhr.ontimeout = () => {
      cleanup();
      reject(new Error("UPLOAD_NETWORK_ERROR"));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new Error("UPLOAD_ABORTED"));
    };

    xhr.send(formData);
  });
};

// Legacy function for backward compatibility
export type ProfileTab = "recent" | "popular" | "favorites";

export const fetchProfileEntries = async (
  userId: number,
  tab: ProfileTab = "recent",
  page: number = 1,
  pageSize: number = 10
): Promise<ProfileEntriesResponse> => {
  // Map old tab names to new ones
  const tabMapping: Record<ProfileTab, PublicProfileTab> = {
    recent: "recent",
    popular: "mostLiked",
    favorites: "mostFavorited",
  };
  return fetchPublicProfileEntries(userId, tabMapping[tab], page, pageSize);
};
// ── Profile Details (Settings → Profile tab) ──

/**
 * Fetch the current user's editable profile details.
 * GET /api/profile/me/details
 */
export const getMyProfileDetails = async (): Promise<ProfileDetailsModel> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/profile/me/details`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    throw new Error("Failed to fetch profile details");
  }
  return response.json();
};

/**
 * Update the current user's editable profile details.
 * PUT /api/profile/me/details
 */
export const updateMyProfileDetails = async (
  request: UpdateProfileDetailsRequest
): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/profile/me/details`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(request),
  });
  if (!response.ok && response.status !== 204) {
    throw new Error("Failed to update profile details");
  }
};